import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ChevronDown } from "lucide-react";
import { COMPANY } from "@/lib/data";
import { useDemoModal } from "@/components/DemoModalContext";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.7 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { setOpen } = useDemoModal();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative h-[100svh] w-full overflow-hidden bg-ink"
      data-testid="hero"
    >
      {/* Video */}
      <motion.div style={{ y: videoY }} className="absolute inset-0 h-[118%] w-full">
        <video
          className="h-full w-full object-cover"
          style={{ filter: "brightness(0.4)" }}
          autoPlay
          muted
          loop
          playsInline
          poster="/hero-poster.jpg"
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
      </motion.div>
      {/* Overlays for legibility */}
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col items-center justify-center px-6 text-center md:px-[100px]"
      >
        <motion.div
          initial="hidden"
          animate="show"
          custom={0}
          variants={fade}
          className="mb-4 flex max-w-3xl items-center justify-center gap-2.5 text-sm font-bold uppercase leading-tight tracking-[0.15em] text-white sm:text-base lg:text-lg"
        >
          <span className="h-2 w-2 shrink-0 rounded-full bg-lime" />
          {COMPANY.subtitle}
        </motion.div>

        <h1 className="text-white">
          <span className="reveal-mask">
            <motion.span
              initial="hidden"
              animate="show"
              custom={0}
              variants={line}
              className="block text-6xl font-black leading-[0.95] tracking-tighter sm:text-7xl lg:text-[8rem] drop-shadow-[0_4px_30px_rgba(0,0,0,0.35)]"
            >
              TEKNOŞURA
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial="hidden"
          animate="show"
          custom={1}
          variants={fade}
          className="mt-6 max-w-2xl text-lg font-medium text-white/85 md:text-xl"
        >
          {COMPANY.slogan}
        </motion.p>
        <motion.p
          initial="hidden"
          animate="show"
          custom={2}
          variants={fade}
          className="mt-3 max-w-xl text-sm text-white/65 md:text-base"
        >
          {COMPANY.heroText}
        </motion.p>

        <motion.div
          initial="hidden"
          animate="show"
          custom={3}
          variants={fade}
          className="mt-10 flex flex-col items-center gap-3 sm:flex-row"
        >
          <button
            onClick={() => setOpen(true)}
            className="btn-primary w-full sm:w-auto"
            data-testid="hero-demo-btn"
          >
            Demo Talebi Oluştur
          </button>
          <a
            href={`https://wa.me/${COMPANY.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-3.5 font-semibold text-white backdrop-blur-md transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-white/15 sm:w-auto"
            data-testid="hero-call-btn"
          >
            <Phone className="h-4 w-4" /> Bizi Arayın
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.a
        href="#stats"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/70"
        aria-label="Aşağı kaydır"
        data-testid="hero-scroll"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-1 text-[10px] uppercase tracking-[0.3em]"
        >
          Keşfet
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </motion.a>
    </section>
  );
}
